import { systemConfigAPI } from './api';
import { DEFAULT_UI_FEATURE_POLICY, UI_FEATURE_POLICY_CONFIG_KEY } from '../config/uiFeaturePolicy.defaults';
import type { UiFeaturePolicy } from '../types/uiAccessPolicy';

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function normalizePolicy(value: unknown): UiFeaturePolicy {
  const fallback = DEFAULT_UI_FEATURE_POLICY;
  const obj = asObject(value);
  if (!obj) return fallback;

  const version = typeof obj.version === 'number' ? obj.version : fallback.version;
  const featuresRaw = asObject(obj.features) ?? {};

  const features = Object.entries(featuresRaw).reduce<UiFeaturePolicy['features']>((acc, [k, v]) => {
    const rule = asObject(v);
    if (!rule) return acc;
    acc[k] = {
      enabledByDefault: typeof rule.enabledByDefault === 'boolean' ? rule.enabledByDefault : true,
      allowedConfigIds: Array.isArray(rule.allowedConfigIds) ? rule.allowedConfigIds.map(String) : [],
      deniedConfigIds: Array.isArray(rule.deniedConfigIds) ? rule.deniedConfigIds.map(String) : [],
      allowedUserIds: Array.isArray(rule.allowedUserIds) ? rule.allowedUserIds.map((x) => Number(x)) : [],
      allowedRoles: Array.isArray(rule.allowedRoles) ? rule.allowedRoles.map(String) : [],
    };
    return acc;
  }, {});

  return {
    version,
    features: {
      ...fallback.features,
      ...features,
    },
    meta: asObject(obj.meta) as UiFeaturePolicy['meta'],
  };
}

export async function loadFeaturePolicy(): Promise<UiFeaturePolicy> {
  try {
    const res = await systemConfigAPI.get(UI_FEATURE_POLICY_CONFIG_KEY);
    return normalizePolicy(res.data?.config_value_json);
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 404) return DEFAULT_UI_FEATURE_POLICY;
    return DEFAULT_UI_FEATURE_POLICY;
  }
}

export async function saveFeaturePolicy(policy: UiFeaturePolicy): Promise<UiFeaturePolicy> {
  const payload = normalizePolicy(policy);
  const res = await systemConfigAPI.set(UI_FEATURE_POLICY_CONFIG_KEY, payload as unknown as Record<string, unknown>);
  return normalizePolicy(res.data?.config_value_json);
}
